import { useEffect, useState } from 'react'
import { View } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, notification, ImpactStyle, NotificationType } from '@/lib/utils/haptics'
import { Mail } from '@/components/ui/icons'
import { AuthErrorBanner } from '../../components/auth/AuthErrorBanner'
import { AuthHeader } from '../../components/auth/AuthHeader'
import { AuthOtpStep } from '../../components/auth/AuthOtpStep'
import { AuthSendCodeStep } from '../../components/auth/AuthSendCodeStep'
import { BackButton } from '../../components/ui/BackButton'
import { AuthScaffold } from '../../components/auth/AuthScaffold'
import { EmailInput } from '../../components/ui/StructuredTextInput'
import { useAuth } from '../../lib/hooks/use-auth'
import { useAuthStore } from '../../lib/stores/auth.store'
import { forgotPasswordSchema } from '../../lib/validations/auth.schema'
import { toast } from '../../lib/toast'
import { captureError, errorMessage } from '../../lib/utils/observability'
import { useSemanticColors } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { SPACING } from '../../lib/brand'

export default function VerifyEmailScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop } = useBreakpoint()
  const {
    user,
    sendEmailVerification,
    verifyEmailChange,
    sendEmailVerificationPending,
    verifyEmailChangePending,
  } = useAuth()
  const setCompletingSignup = useAuthStore((s) => s.setCompletingSignup)

  // Never re-type an email we already know from the session.
  const seededEmail = (() => {
    const raw = user?.email?.trim().toLowerCase() ?? ''
    if (!raw) return ''
    if (raw.endsWith('@phone.tago.app') || raw.endsWith('@anon.tago.app')) return ''
    return raw
  })()
  const [email, setEmail] = useState(seededEmail)
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    setCompletingSignup(false)
  }, [setCompletingSignup])

  useEffect(() => {
    if (seededEmail && !email) setEmail(seededEmail)
  }, [seededEmail, email])

  const bodyStyle = isDesktop ? undefined : { flexGrow: 1, justifyContent: 'center' as const }

  const handleSend = async () => {
    impact(ImpactStyle.Light)
    setError('')

    const parsed = forgotPasswordSchema.safeParse({ email })
    if (!parsed.success) {
      setError(t('auth.validation.email_invalid'))
      void notification(NotificationType.Error)
      return
    }

    try {
      await sendEmailVerification(parsed.data.email)
      setPendingEmail(parsed.data.email)
      void notification(NotificationType.Success)
      toast.success(t('auth.verify_email_sent'))
    } catch (err) {
      setError(errorMessage(err, t('auth.errors.email_verification_failed')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-email', action: 'send' })
    }
  }

  const handleComplete = async (code: string) => {
    if (!pendingEmail) return
    setError('')
    impact(ImpactStyle.Light)

    try {
      await verifyEmailChange({ email: pendingEmail, code })
      void notification(NotificationType.Success)
      router.replace('/(tabs)/explore' as never)
    } catch (err) {
      setError(errorMessage(err, t('auth.errors.otp_invalid')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-email', action: 'verify' })
      throw err
    }
  }

  const handleResend = async () => {
    if (!pendingEmail) return
    setError('')
    impact(ImpactStyle.Light)
    try {
      await sendEmailVerification(pendingEmail)
      toast.success(t('auth.verify_email_sent'))
    } catch (err) {
      toast.error(errorMessage(err, t('auth.errors.email_verification_failed')))
      void notification(NotificationType.Error)
      captureError(err, { screen: 'verify-email', action: 'resend' })
      throw err
    }
  }

  const formContent = (
    <>
      <BackButton testID="verify-email.back-button" style={{ marginBottom: SPACING.sm }} />

      {/* One field and one button ended their block barely past the halfway
          line and left the bottom 45% of the phone empty. The stacked layout
          hands this body the whole viewport, so grow into it and centre the
          block under the pinned back button; the split layout centres its own
          column already. */}
      <View style={bodyStyle}>
        <AuthHeader
          title={pendingEmail ? t('auth.verify_email_title') : t('auth.link_email_title')}
          subtitle={
            pendingEmail
              ? t('auth.verify_email_code_desc', { email: pendingEmail })
              : t('auth.link_email_desc')
          }
        />

        {pendingEmail ? (
          <AuthOtpStep
            onComplete={handleComplete}
            onResend={handleResend}
            disabled={verifyEmailChangePending || sendEmailVerificationPending}
            error={error}
            testID="verify-email.otp-input"
          />
        ) : (
          <AuthSendCodeStep
            buttonTitle={t('auth.verify_email_send_code')}
            onSend={handleSend}
            loading={sendEmailVerificationPending}
            testID="verify-email.send-button"
          >
            <EmailInput
              label={t('auth.email')}
              placeholder={t('auth.email_placeholder')}
              value={email}
              onChangeText={(value) => {
                setEmail(value)
                setError('')
              }}
              error={error || undefined}
              leftIcon={<Mail size={18} color={colors.textMuted} />}
              testID="verify-email.email-input"
            />
          </AuthSendCodeStep>
        )}

        <AuthErrorBanner error={pendingEmail ? error || null : null} marginTop />
      </View>
    </>
  )

  return (
    <AuthScaffold
      brandHeadline={t('welcome.headline')}
      brandSubtitle={t('welcome.subheadline')}
      background={colors.background}
    >
      {formContent}
    </AuthScaffold>
  )
}
